import type { SolveSession, CodeHistory } from '@yandex-int/k-common/typings';
import { retrieveFromSessionStorage, saveToSessionStorage } from '../../../../utils/web-storage';

/**
 * Describes a locally-saved code session (undo/redo history and related events).
 */
export interface LocalCodeSession {
    readonly text: string;
    readonly history?: CodeHistory;
}

/**
 * Saves a local code session to
 * [session storage](https://developer.mozilla.org/en-US/docs/Web/API/Window/sessionStorage)
 * under the specified key.
 */
export function saveCodeSession(sessionKey: string, session: LocalCodeSession): void {
    saveToSessionStorage(sessionKey, session.text);
    if (session.history) {
        saveToSessionStorage(derivedKeyForHistory(sessionKey), JSON.stringify(session.history.toUndo));
    }
}

/**
 * Gets a previously saved local code session from
 * [session storage](https://developer.mozilla.org/en-US/docs/Web/API/Window/sessionStorage)
 * under the specified key.
 *
 * Saved sassion may miss the history part and only include the text in case
 * it was saved in the previous versions of the feature.
 */
export function restoreCodeSession(sessionKey: string): LocalCodeSession | null {
    const text = retrieveFromSessionStorage(sessionKey);
    if (text === null) {
        return null;
    }

    let history: CodeHistory | undefined;
    const serializedChanges = retrieveFromSessionStorage(derivedKeyForHistory(sessionKey));
    if (serializedChanges) {
        let changes: CodeHistory['toUndo'] | undefined;
        try {
            changes = JSON.parse(serializedChanges);
        } catch (err) {
            console.error('Invalid format for serialized code changes', err);
        }

        if (Array.isArray(changes)) {
            history = { lastText: text, toUndo: changes, toRedo: [] };
        } else {
            console.error('Found non-array for code changes, ignored.');
        }
    }

    return { text, history };
}

function derivedKeyForHistory(baseSessionKey: string): string {
    return `${baseSessionKey}-changes`;
}

export function codeSessionFromLocal(session: LocalCodeSession): SolveSession {
    const history: SolveSession['history'] = session.history ?? {
        lastText: session.text,
        toUndo: [],
        toRedo: [],
    };
    return {
        history,
        pasteDetected: history.toUndo.some((event) => event.pastes?.some((p) => p.external)),
    };
}
